import { io, Socket } from 'socket.io-client';

// Get environment variables
const SOCKET_URL = import.meta.env.VITE_SOCKET_URL || 'http://localhost:9000';

/** Admin namespace path – connect to this for admin tools (separate from game socket). */
export const ADMIN_SOCKET_NAMESPACE = '/admin';

// Socket.IO client configuration
export const socketConfig = {
    url: SOCKET_URL,
    options: {
        // Transports: try WebSocket first, fallback to polling
        transports: ['websocket', 'polling'],

        // Auto-reconnection settings
        reconnection: true,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
        reconnectionAttempts: 5,

        // Connection timeout
        timeout: 20000,

        // Enable CORS
        withCredentials: true,

        // Auto-connect on creation
        autoConnect: true,

        // Path for Socket.IO (default is /socket.io)
        path: '/socket.io/',
    }
};

// Create and export socket instance – one token only (user or guest JWT from authToken)
export const createSocket = (auth?: { token?: string }): Socket => {
    const options = {
        ...socketConfig.options,
        ...(auth?.token && {
            auth: { token: auth.token },
        }),
    };

    const socket = io(socketConfig.url, options);

    // Connection event handlers
    socket.on('connect', () => {
        console.log('✅ Socket connected:', socket.id);
    });

    socket.on('disconnect', (reason) => {
        console.log('❌ Socket disconnected:', reason);
    });

    socket.on('connect_error', (error) => {
        console.error('🔴 Socket connection error:', error.message);
    });

    socket.on('reconnect', (attemptNumber) => {
        console.log('🔄 Socket reconnected after', attemptNumber, 'attempts');
    });

    socket.on('reconnect_error', (error) => {
        console.error('🔴 Socket reconnection error:', error.message);
    });

    socket.on('reconnect_failed', () => {
        console.error('🔴 Socket reconnection failed');
    });

    return socket;
};

/**
 * Create a socket connected to the admin namespace (/admin).
 * Use only on AdminToolsPage; do not reuse the game socket for admin.
 * @param auth - Must include token (user JWT; admin role required by server).
 */
export const createAdminSocket = (auth: { token: string }): Socket => {
    const options = {
        ...socketConfig.options,
        auth: { token: auth.token },
    };

    const socket = io(socketConfig.url + ADMIN_SOCKET_NAMESPACE, options);

    socket.on('connect', () => {
        console.log('✅ Admin socket connected:', socket.id);
    });

    socket.on('disconnect', (reason) => {
        console.log('❌ Admin socket disconnected:', reason);
    });

    socket.on('connect_error', (error) => {
        console.error('🔴 Admin socket connection error:', error.message);
    });

    socket.on('reconnect', (attemptNumber) => {
        console.log('🔄 Admin socket reconnected after', attemptNumber, 'attempts');
    });

    socket.on('reconnect_error', (error) => {
        console.error('🔴 Admin socket reconnection error:', error.message);
    });

    socket.on('reconnect_failed', () => {
        console.error('🔴 Admin socket reconnection failed');
    });

    return socket;
};

// Export API base URL
export const API_BASE_URL = import.meta.env.VITE_BACKEND_URL || 'http://localhost:9000';